import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { metaAdsCardSx, metaAdsSkeletonSx } from './metaAdsUi.styles';

/*
 * Placeholders de Análisis, Presupuesto e Informe: misma grilla, interlineado, separadores y
 * paddings que el contenido real (`MetaDataParts`), así el layout no salta al resolver.
 * Cada barra va centrada en una caja del alto de la línea de texto que reemplaza.
 */

const dividerSx = '1px solid rgba(255,255,255,0.07)';
const range = (n) => Array.from({ length: n }, (_, i) => i);
const WIDTHS = ['92%', '64%', '80%', '48%', '72%', '56%'];

/** Barra centrada en una línea de `lineHeight` px. */
function Line({ lineHeight = 20, height = 12, width = '100%', radius }) {
  return (
    <Box sx={{ height: lineHeight, display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'inherit' }}>
      <Skeleton variant="rounded" height={height} width={width} sx={{ ...metaAdsSkeletonSx, ...(radius ? { borderRadius: radius } : null) }} />
    </Box>
  );
}

/** Como `MetaBarList`: nombre · barra · valor. */
export function MetaBarsSkeleton({ rows = 3 }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '64px minmax(40px, 1fr) 56px', columnGap: 1.25, rowGap: 1.1, alignItems: 'center' }}>
      {range(rows).map((i) => (
        <React.Fragment key={i}>
          <Line />
          <Skeleton variant="rounded" height={8} width={WIDTHS[i % WIDTHS.length]} sx={metaAdsSkeletonSx} />
          <Line />
        </React.Fragment>
      ))}
    </Box>
  );
}

/** Como `MetaDonut`: dona de 96 px + leyenda. */
export function MetaDonutSkeleton({ rows = 3 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
      <Skeleton variant="circular" width={96} height={96} sx={{ ...metaAdsSkeletonSx, flexShrink: 0 }} />
      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) 72px', columnGap: 1, rowGap: 0.9, alignItems: 'center' }}>
        {range(rows).map((i) => (
          <React.Fragment key={i}>
            <Skeleton variant="rounded" height={10} width={10} sx={metaAdsSkeletonSx} />
            <Line width={WIDTHS[i % WIDTHS.length]} />
            <Line />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

/** Como `MetaTable`: encabezado chico y filas con separador; números a la derecha. */
export function MetaTableSkeleton({ rows = 3, columns = 2 }) {
  const template = ['minmax(0, 1fr)', ...range(columns).map(() => '64px')].join(' ');
  const cellSx = (c) => ({ borderBottom: dividerSx, pl: c === 0 ? 0 : 2, display: 'flex', justifyContent: c === 0 ? 'flex-start' : 'flex-end' });
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: template }}>
      {range(columns + 1).map((c) => (
        <Box key={`h-${c}`} sx={{ ...cellSx(c), py: 0.75 }}>
          <Line lineHeight={16} height={8} width={c === 0 ? 48 : 40} />
        </Box>
      ))}
      {range(rows).map((r) => range(columns + 1).map((c) => (
        <Box key={`${r}-${c}`} sx={{ ...cellSx(c), py: 1 }}>
          <Line width={c === 0 ? WIDTHS[r % WIDTHS.length] : '100%'} />
        </Box>
      )))}
    </Box>
  );
}

/** Como `MetaStatRows`: nombre a la izquierda, valor a la derecha, separador fino. */
export function MetaStatRowsSkeleton({ rows = 4 }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 72px' }}>
      {range(rows).map((i) => (
        <React.Fragment key={i}>
          <Box sx={{ py: 0.9, pr: 2, borderBottom: dividerSx }}><Line width={WIDTHS[i % WIDTHS.length]} /></Box>
          <Box sx={{ py: 0.9, borderBottom: dividerSx }}><Line /></Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

/** Como `MetaStatTile`: número grande + nombre. */
function TileSkeleton() {
  return (
    <Box sx={{ flex: 1, minWidth: 0, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.05)', px: 1.5, py: 1.25 }}>
      <Line lineHeight={26} height={18} width="55%" />
      <Box sx={{ mt: 0.25 }}><Line lineHeight={16} height={10} width="80%" /></Box>
    </Box>
  );
}

/** Tarjeta (`MetaCard`) con la barra del título en su fila de 24 px. */
function CardSkeleton({ titleWidth = 120, children }) {
  return (
    <Box sx={metaAdsCardSx}>
      <Line lineHeight={24} height={14} width={titleWidth} />
      {children}
    </Box>
  );
}

/** Público configurado: un bloque con línea guía por conjunto (nombre + líneas de segmentación). */
export function MetaAdSetsSkeleton({ rows = 2 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {range(rows).map((i) => (
        <Box key={i} sx={{ borderLeft: '2px solid rgba(255,255,255,0.12)', bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '0 8px 8px 0', px: 1.25, py: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Line width={i ? '50%' : '65%'} />
          <Line lineHeight={17} height={10} width="85%" />
          <Line lineHeight={17} height={10} width="60%" />
        </Box>
      ))}
    </Box>
  );
}

/** Como `MetaGroupLabel`: subtítulo chico en mayúsculas. */
function GroupLabelSkeleton({ width = 140 }) {
  return <Box sx={{ mt: 0.5 }}><Line lineHeight={16} height={8} width={width} /></Box>;
}

/** Interacciones: alcance / impresiones, tarjeta «Interacciones» (mosaicos + dos grupos) y «Video». */
export function MetaInteractionsSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.25 }}><TileSkeleton /><TileSkeleton /></Box>
      <CardSkeleton titleWidth={110}>
        <Box sx={{ display: 'flex', gap: 1.25 }}><TileSkeleton /><TileSkeleton /></Box>
        <GroupLabelSkeleton width={150} />
        <MetaStatRowsSkeleton rows={5} />
        <GroupLabelSkeleton width={110} />
        <MetaStatRowsSkeleton rows={4} />
      </CardSkeleton>
      <CardSkeleton titleWidth={56}>
        <MetaStatRowsSkeleton rows={4} />
      </CardSkeleton>
    </Box>
  );
}

/** Como `MetaFootnote`: líneas de 16 px. */
function FootnoteSkeleton({ lines = 1 }) {
  return <Box>{range(lines).map((i) => <Line key={i} lineHeight={16} height={10} width={i === lines - 1 ? '60%' : '95%'} />)}</Box>;
}

/** Presupuesto: resumen por moneda + tarjetas de campaña (etiqueta, nombre, nivel, ⋮ ⌄, filas, uso). */
export function MetaBudgetSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="meta-budget-skeleton" aria-busy="true">
      <CardSkeleton titleWidth={200}>
        <MetaStatRowsSkeleton rows={4} />
        <FootnoteSkeleton lines={2} />
      </CardSkeleton>
      {[0, 1].map((i) => (
        <Box key={i} sx={{ ...metaAdsCardSx, gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Line lineHeight={18} height={18} width={64} radius="4px" />
              <Line width={i ? '55%' : '75%'} height={14} />
              <Line lineHeight={20} height={20} width={120} radius="999px" />
            </Box>
            <Box sx={{ display: 'flex', gap: '4px', mt: '5px', flexShrink: 0 }}>
              <Skeleton variant="circular" width={28} height={28} sx={metaAdsSkeletonSx} />
              <Skeleton variant="circular" width={28} height={28} sx={metaAdsSkeletonSx} />
            </Box>
          </Box>
          <MetaStatRowsSkeleton rows={3} />
          <Box>
            <Box sx={{ mb: 0.5 }}><Line lineHeight={16} height={10} width="60%" /></Box>
            <Skeleton variant="rounded" height={8} width="100%" sx={metaAdsSkeletonSx} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Una fila de «Por propiedad»: nombre con «Ver informe» a la derecha, anuncios y tres métricas. */
function ReportUnitSkeleton() {
  return (
    <Box sx={{ borderTop: dividerSx, pt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Line width="55%" height={14} />
          <Line lineHeight={16} height={10} width={72} />
        </Box>
        <Box sx={{ width: 72, flexShrink: 0 }}><Line height={12} width="100%" /></Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: 1.5 }}>
        {range(3).map((c) => (
          <Box key={c}>
            <Line lineHeight={16} height={8} width="70%" />
            <Line width="85%" />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Informe: «Resumen del espacio» + tarjeta «Por propiedad». */
export function MetaReportSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="meta-report-skeleton" aria-busy="true">
      <CardSkeleton titleWidth={150}>
        <MetaStatRowsSkeleton rows={6} />
        <FootnoteSkeleton />
      </CardSkeleton>
      <CardSkeleton titleWidth={110}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <ReportUnitSkeleton />
          <ReportUnitSkeleton />
        </Box>
        <FootnoteSkeleton lines={2} />
      </CardSkeleton>
    </Box>
  );
}
